import { useCallback, useMemo } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import type { UserRole } from '@/types';

type Permission =
  | 'manage_users'
  | 'manage_institution'
  | 'manage_groups'
  | 'manage_projects'
  | 'publish_eduflow'
  | 'view_analytics'
  | 'manage_store'
  | 'manage_audiobooks'
  | 'manage_qualiopi'
  | 'manage_budget'
  | 'manage_campaigns'
  | 'manage_competencies'
  | 'approve_requests'
  | 'view_reports'
  | 'manage_certificates'
  | 'manage_content_pipeline'
  | 'view_producer_assignments';

const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: [
    'manage_users', 'manage_institution', 'manage_groups', 'manage_projects',
    'publish_eduflow', 'view_analytics', 'manage_store', 'manage_audiobooks',
    'manage_qualiopi', 'manage_budget', 'manage_campaigns', 'manage_competencies',
    'approve_requests', 'view_reports', 'manage_certificates',
  ],
  institution: [
    'manage_institution', 'manage_groups', 'manage_projects', 'publish_eduflow',
    'view_analytics', 'manage_store', 'manage_audiobooks', 'manage_qualiopi',
    'manage_budget', 'manage_campaigns', 'manage_competencies', 'approve_requests',
    'view_reports', 'manage_certificates', 'manage_content_pipeline',
  ],
  institution_admin: [
    'manage_institution', 'manage_groups', 'manage_projects', 'publish_eduflow',
    'view_analytics', 'manage_store', 'manage_audiobooks', 'manage_qualiopi',
    'manage_budget', 'manage_campaigns', 'manage_competencies', 'approve_requests',
    'view_reports', 'manage_certificates', 'manage_content_pipeline',
  ],
  resp_formation: [
    'manage_projects', 'publish_eduflow', 'manage_audiobooks', 'view_analytics',
    'manage_certificates', 'view_producer_assignments', 'manage_content_pipeline',
  ],
  drh: [
    'manage_budget', 'manage_campaigns', 'manage_competencies', 'approve_requests',
    'view_reports', 'view_analytics', 'manage_qualiopi', 'manage_content_pipeline',
  ],
  rh: [
    'manage_campaigns', 'manage_competencies', 'approve_requests', 'view_reports',
    'view_analytics', 'manage_content_pipeline',
  ],
  manager: [
    'manage_groups', 'approve_requests', 'view_reports', 'view_analytics',
  ],
  teacher: [
    'manage_projects', 'publish_eduflow', 'manage_audiobooks', 'view_analytics',
    'manage_certificates', 'view_producer_assignments',
  ],
  collaborator: [
    'view_reports',
  ],
  learner: [],
  student: [],
};

export function useAuth() {
  const { user, isAuthenticated, isLoading, login, logout, updateProfile } = useAuthStore();

  const isAdmin = useMemo(() => user?.role === 'admin', [user?.role]);
  const isTeacher = useMemo(() => user?.role === 'teacher', [user?.role]);
  const isInstitution = useMemo(() => user?.role === 'institution', [user?.role]);
  const isLearner = useMemo(() => user?.role === 'learner', [user?.role]);

  const hasRole = useCallback(
    (role: UserRole) => user?.role === role,
    [user?.role],
  );

  const hasAnyRole = useCallback(
    (...roles: UserRole[]) => !!user && roles.includes(user.role),
    [user],
  );

  const can = useCallback(
    (permission: Permission): boolean => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      const permissions = ROLE_PERMISSIONS[user.role] || [];
      return permissions.includes(permission);
    },
    [user],
  );

  const fullName = useMemo(
    () => (user ? `${user.firstName} ${user.lastName}` : ''),
    [user],
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateProfile,
    isAdmin,
    isTeacher,
    isInstitution,
    isLearner,
    hasRole,
    hasAnyRole,
    can,
    fullName,
  };
}
